import { ChakraBox, ChakraFlex } from '@equip.health/ui';
import { AppointmentStatusCounts } from '../constants';
import {
  getBorderRadius,
  getVisibleStatusKeys,
  STATUS_COLORS,
  STATUS_KEYS,
  STATUS_LABELS,
} from './statusUtils';

interface MyDayStackedBarProps {
  appointments: AppointmentStatusCounts;
  hoveredIndex: number | null;
  onSegmentHover: (index: number | null) => void;
  onTrackSegmentHover?: (statusKey: string, count: number) => void;
}

const MyDayStackedBar = ({
  appointments,
  hoveredIndex,
  onSegmentHover,
  onTrackSegmentHover,
}: MyDayStackedBarProps) => {
  const visibleSegments = getVisibleStatusKeys(appointments);

  return (
    <ChakraFlex
      data-testid="my-day-stacked-bar"
      height="14px"
      gap="3px"
      width="100%"
      aria-label="Today's appointment status breakdown"
    >
      {STATUS_KEYS.map((key, index) => {
        const count = appointments[key];
        if (!count) return null;
        const flexGrow = count / appointments.total;
        const isFirst = visibleSegments[0] === key;
        const isLast = visibleSegments[visibleSegments.length - 1] === key;
        return (
          <ChakraBox
            key={key}
            data-testid={`my-day-bar-segment-${key}`}
            bg={STATUS_COLORS[key]}
            flex={flexGrow}
            height="100%"
            borderRadius={getBorderRadius(isFirst, isLast)}
            cursor="pointer"
            opacity={hoveredIndex !== null && hoveredIndex !== index ? 0.35 : 1}
            transform={hoveredIndex === index ? 'scaleY(1.18)' : 'scaleY(1)'}
            transition="opacity 150ms ease, transform 150ms ease"
            onMouseEnter={() => {
              onSegmentHover(index);
              onTrackSegmentHover?.(key, count);
            }}
            onMouseLeave={() => onSegmentHover(null)}
            position="relative"
          >
            {hoveredIndex === index && (
              <ChakraFlex
                position="absolute"
                bottom="calc(100% + 8px)"
                left="50%"
                transform="translateX(-50%)"
                bg="white"
                boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                borderRadius="8px"
                padding="8px 12px"
                alignItems="center"
                gap="8px"
                whiteSpace="nowrap"
                zIndex={10}
              >
                <ChakraBox
                  width="8px"
                  height="8px"
                  borderRadius="50%"
                  bg={STATUS_COLORS[key]}
                />
                <ChakraBox fontSize="12px" fontWeight="400">
                  {STATUS_LABELS[key]}
                </ChakraBox>
                <ChakraBox fontSize="12px" fontWeight="600">
                  {count}
                </ChakraBox>
              </ChakraFlex>
            )}
          </ChakraBox>
        );
      })}
    </ChakraFlex>
  );
};

export default MyDayStackedBar;
